import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { AlertService } from '../../core';
import { BaseComponent } from '../base.component';
import { ChangesDisplayService } from '../changes/changes-display.service';
import { AlertDialogComponent } from '../dialogs';
import { IChangedFieldDetail } from '../pay-categories/pay-categories.type';
import { PayScalesService } from './pay-scales.service';
import {
	IPayScaleEffectiveDate,
	IMinimumEffectiveDateResponse,
	IPayScale,
	IPayScaleGlobalSettings,
	IPayScaleHistory,
	IYearOfServiceRecords
} from './pay-scales.types';

@Component({
	selector: 'app-pilotpay-pay-scales-details',
	templateUrl: './pay-scales-details.component.html'
})
export class PayScalesDetailsComponent extends BaseComponent implements OnInit {
	constructor(
		protected alertService: AlertService,
		private router: Router,
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private changeDisplayService: ChangesDisplayService,
		private payScalesService: PayScalesService
	) {
		super(alertService);
	}

	public hasFutureChange: boolean = false;
	public id: string = '';
	public result: string = '';
	public payScaleId: number = 0;
	public futureChangesToggle: boolean = false;
	public historyToggle: boolean = false;
	public effectiveStartDate: IPayScaleEffectiveDate;
	public isNeedDiscard: boolean = false;
	public payPeriods: IPayScaleEffectiveDate[];
	public payScale: IPayScale;
	public payScaleSetting: IPayScaleGlobalSettings;
	public payScaleHistory: IChangedFieldDetail[];
	public payScaleChanges: IChangedFieldDetail[];
	public yearOfServiceRecord: IYearOfServiceRecords = {
		yearsOfService: 1,
		constantAPBonus: 0,
		guaranteedHours: 0,
		hourlyRateDollars: 0,
		instructorPayDollars: 0
	};
	public isAddRecord: boolean = true;
	public dialogConfig: MatDialogConfig;
	public alertDialogRef: MatDialogRef<AlertDialogComponent>;

	private readonly externalFormat: string = 'MMMM Y';
	private readonly internalFormat: string = 'YYYY-MM-DDTHH:mm:ss';

	ngOnInit(): void {
		this.id = this.route.snapshot.paramMap.get('id');
		this.dialogConfig = new MatDialogConfig();
		if (this.id) {
			this.payScaleId = parseInt(this.id, 10);
		}
		this.payScale = {
			yearOfServiceRecords: [],
			perTripPay: 0,
			constantApBonus: 0,
			availabilityPay: 0,
			isApRatingPayScale: false,
			displayGuaranteedHours: false,
			isInstructorPayScale: false
		};
		this.payScale.yearOfServiceRecords = [...this.payScale.yearOfServiceRecords, this.yearOfServiceRecord];
		if (this.payScaleId) {
			this.getPayScale();
		}
		this.getPayScaleSetting();
	}

	/**
	 * isNeedChange
	 */
	public isNeedChange(payScale?: IPayScale): void {
		this.isNeedDiscard = true;
		if (payScale) {
			this.payScale = payScale;
		}
	}

	/**
	 * onYearOfServiceRecordsChange
	 */
	public onYearOfServiceRecordsChange(yearOfServiceRecords: IYearOfServiceRecords[]): void {
		this.isNeedDiscard = true;
		this.payScale.yearOfServiceRecords = [];
		if (yearOfServiceRecords) {
			this.payScale.yearOfServiceRecords = [...[], ...yearOfServiceRecords];
		}
	}

	/**
	 * getPayScale
	 */
	public getPayScale(): void {
		this.payScalesService.getPayScale(this.payScaleId).subscribe(
			(DATA: IPayScale[]) => {
				if (DATA && DATA.length) {
					this.payScale = DATA[0];
					let yearsOfService: number = 1;
					if (this.payScale && this.payScale.yearOfServiceRecords) {
						yearsOfService = this.payScale.yearOfServiceRecords.length + 1;
					}
					this.yearOfServiceRecord = {
						yearsOfService: yearsOfService,
						constantAPBonus: 0,
						guaranteedHours: 0,
						hourlyRateDollars: 0,
						instructorPayDollars: 0
					};
					if (!this.payScale.yearOfServiceRecords) {
						this.payScale.yearOfServiceRecords = [];
					}
					const yearOfService: IYearOfServiceRecords = {
						yearsOfService: yearsOfService,
						constantAPBonus: 0,
						guaranteedHours: 0,
						hourlyRateDollars: 0,
						instructorPayDollars: 0
					};
					this.getMinimumEffectiveDate();
					this.getPayScaleFutureChanges();
					this.getPayScaleHistory();
					this.payScale.yearOfServiceRecords = [...this.payScale.yearOfServiceRecords, yearOfService];
				}
			},
			(error: HttpErrorResponse) => {
				this.handleError(error);
			}
		);
	}

	/**
	 * createPayScale
	 */
	public createPayScale(): void {
		this.payScale.effectiveStart = this.effectiveStartDate.internalFormat;

		if (!this.payScale.yearOfServiceRecords) {
			this.payScale.yearOfServiceRecords = [];
		}

		const yearOfServiceRecords: IYearOfServiceRecords[] = [];

		if (!this.payScale.isApRatingPayScale && this.payScale.yearOfServiceRecords.length > 0) {
			this.payScale.yearOfServiceRecords.map((record: IYearOfServiceRecords) => {
				if (record.hourlyRateDollars > 0) {
					yearOfServiceRecords.push(record);
				}
			});
			this.payScale.yearOfServiceRecords = [...[], ...yearOfServiceRecords];
		}

		this.payScalesService.createPayScale(this.payScale).subscribe(
			(DATA: IPayScale) => {
				if (DATA) {
					this.payScale = DATA;
					this.navigate();
				}
			},
			(error: HttpErrorResponse) => {
				this.handleError(error);
			}
		);
	}

	/**
	 * getMinimumEffectiveDate
	 *
	 */
	public getMinimumEffectiveDate(): void {
		this.payScalesService.getMinimumEffectiveDate(this.payScaleId).subscribe(
			(DATA: IMinimumEffectiveDateResponse) => {
				if (DATA && DATA.minimumEffectiveDate) {
					this.hasFutureChange = false;

					const minEffectiveDate: moment.Moment = moment.utc(DATA.minimumEffectiveDate);

					this.effectiveStartDate = {
						externalFormat: minEffectiveDate.format(this.externalFormat),
						internalFormat: minEffectiveDate.format(this.internalFormat)
					};
					this.payPeriods = [this.effectiveStartDate];

					for (let i: number = 1; i < 13; i++) {
						const followingMonth: moment.Moment = minEffectiveDate.clone();
						followingMonth.add(i, 'month');

						this.payPeriods.push({
							externalFormat: followingMonth.format(this.externalFormat),
							internalFormat: followingMonth.format(this.internalFormat)
						});
					}

					const previousDate: string = moment()
						.subtract(1, 'month')
						.set('date', 1)
						.startOf('day')
						.format(this.externalFormat);

					if (this.effectiveStartDate.externalFormat === previousDate) {
						this.payPeriods[0].externalFormat += ' (Previous)';
						this.payPeriods[1].externalFormat += ' (Current)';
					} else {
						this.payPeriods[0].externalFormat += ' (Current)';
					}
				} else {
					this.hasFutureChange = true;
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	/**
	 * getPayScaleSetting
	 *
	 */
	public getPayScaleSetting(): void {
		this.getMinimumEffectiveDate();
		this.payScalesService.getPayScaleSetting().subscribe(
			(DATA: IPayScaleGlobalSettings) => {
				this.payScaleSetting = DATA;
				this.payScale.constantApBonus = this.payScaleSetting.apBonusPercentage;
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	/**
	 * getPayScaleHistory
	 *
	 */
	public getPayScaleHistory(): void {
		this.payScaleHistory = [];
		this.payScalesService.getHistory(this.payScaleId).subscribe(
			(DATA: IPayScaleHistory[]) => {
				if (DATA) {
					const history: IChangedFieldDetail[] = this.changeDisplayService.convertPayScaleHistoryToChangedFieldDetail(
						DATA
					);
					this.payScaleHistory = [...this.payScaleHistory, ...history];
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	/**
	 * getPayScaleFutureChanges
	 *
	 */
	public getPayScaleFutureChanges(): void {
		this.payScaleChanges = [];
		this.payScalesService.getScheduledChanges(this.payScaleId).subscribe(
			(DATA: IPayScaleHistory[]) => {
				if (DATA) {
					const history: IChangedFieldDetail[] = this.changeDisplayService.convertPayScaleHistoryToChangedFieldDetail(
						DATA
					);
					this.payScaleChanges = [...this.payScaleChanges, ...history];
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	/**
	 * updatePayScale
	 */
	public updatePayScale(): void {
		this.payScale.effectiveStart = this.effectiveStartDate.internalFormat;

		// Creating a separate copy of the pay scale prevents the UI from sending any
		// unnecessary properties to the API. Deleting these properties directly from
		// the pay scale will cause issues if the API returns an error since the UI
		// pay scale state is different.
		const payload: IPayScale = JSON.parse(JSON.stringify(this.payScale));
		const yearOfServiceRecords: IYearOfServiceRecords[] = [];

		if (this.payScale.yearOfServiceRecords.length > 0) {
			this.payScale.yearOfServiceRecords.map((record: IYearOfServiceRecords) => {
				const recordCopy: IYearOfServiceRecords = JSON.parse(JSON.stringify(record));
				const isNeedUpdate: boolean = recordCopy.isNeedUpdate;
				delete recordCopy.isNeedUpdate;

				if (isNeedUpdate && recordCopy.hourlyRateDollars > 0) {
					yearOfServiceRecords.push(recordCopy);
				}
			});
			payload.yearOfServiceRecords = [...[], ...yearOfServiceRecords];
		}

		this.payScalesService.updatePayScale(payload).subscribe(
			(DATA: IPayScale) => {
				if (DATA) {
					this.payScale = DATA;
					this.navigate();
				}
			},
			(error: HttpErrorResponse) => {
				this.handleError(error);
			}
		);
	}

	/**
	 * deletePayCategoryFutureChanges
	 */
	public deletePayScaleFutureChanges(): void {
		this.dialogConfig.disableClose = false;
		this.dialogConfig.autoFocus = true;
		this.dialogConfig.data = {
			modalType: 'deleteFutureChanges',
			name: 'Future Changes'
		};
		if (this.dialog) {
			this.alertDialogRef = this.dialog.open(AlertDialogComponent, this.dialogConfig);
		}
		if (this.alertDialogRef) {
			this.alertDialogRef.beforeClosed().subscribe((result: string) => {
				this.result = result;
			});
			this.alertDialogRef.afterClosed().subscribe((isTrue: boolean) => {
				this.alertDialogRef = null;
				if (isTrue) {
					this.payScalesService.deleteScheduledChanges(this.payScaleId).subscribe(
						() => {
							this.payScaleChanges = [];
							this.getMinimumEffectiveDate();
						},
						(error: HttpErrorResponse) => {
							this.handleError(error);
						}
					);
				}
			});
		}
	}

	/**
	 * navigate
	 */
	public navigate(): void {
		this.router.navigate(['/pilotpay/pay-scales']).then(() => {
			this.isNeedDiscard = false;
		});
	}
}
